// resources/js/utils/axiosUtils.ts
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'

type RetryableAxiosRequestConfig = AxiosRequestConfig & {
  _retriedAfterCsrfRefresh?: boolean
}

axios.defaults.withCredentials = true
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
axios.defaults.headers.common['Accept'] = 'application/json'
axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN'

function getCsrfTokenFromMeta(): string | null {
  const meta = document.head.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  return meta?.content || null
}

function setCsrfToken(token: string): void {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = token

  const metaTag = document.head.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  if (metaTag) {
    metaTag.content = token
  }
}

const metaToken = getCsrfTokenFromMeta()
if (metaToken) {
  setCsrfToken(metaToken)
}

let refreshingCsrf: Promise<boolean> | null = null

export async function refreshCsrfToken(): Promise<boolean> {
  if (!refreshingCsrf) {
    refreshingCsrf = axios
      .get('/csrf-token', { withCredentials: true })
      .then(response => {
        const token = response.data?.token

        if (!token) {
          console.warn('CSRF token was not returned')
          return false
        }

        setCsrfToken(token)

        return true
      })
      .catch(err => {
        console.warn('Failed to refresh CSRF token', err)
        return false
      })
      .finally(() => {
        refreshingCsrf = null
      })
  }

  return refreshingCsrf
}

axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error?.response?.status
    const originalRequest = error?.config as RetryableAxiosRequestConfig | undefined

    if (status === 419 && originalRequest && !originalRequest._retriedAfterCsrfRefresh) {
      originalRequest._retriedAfterCsrfRefresh = true

      const refreshed = await refreshCsrfToken()

      if (refreshed) {
        const token = getCsrfTokenFromMeta()
        if (token) {
          originalRequest.headers = {
            ...(originalRequest.headers ?? {}),
            'X-CSRF-TOKEN': token
          }
        }

        return axios(originalRequest)
      }
    }

    return Promise.reject(error)
  }
)

export default axios
